import { ExternalLink } from "lucide-react";
import { EXAMS, PROGRAM_DOCS } from "../data/exams";
import { GuideTabs } from "../components/GuideTabs";
import { ExamBadge, useDocumentTitle } from "../components/ui";
import { pick, useLang, useT, type L } from "../i18n";

/** Khóa học miễn phí trên Anthropic Academy (anthropic.skilljar.com). */
const COURSES: { title: string; desc: L }[] = [
  { title: "AI Fluency: Framework & Foundations", desc: { vi: "Khung 4D: Delegation, Description, Discernment, Diligence.", en: "The 4D framework: Delegation, Description, Discernment, Diligence." } },
  { title: "Claude 101", desc: { vi: "Làm quen Claude.ai: Projects, Artifacts, connectors.", en: "Getting started with Claude.ai: Projects, Artifacts, connectors." } },
  { title: "Claude Code 101 / Claude Code in Action", desc: { vi: "Dùng Claude Code trong dự án thật: CLAUDE.md, hooks, MCP.", en: "Claude Code on real projects: CLAUDE.md, hooks, MCP." } },
  { title: "Agent Skills & Subagents", desc: { vi: "Đóng gói kỹ năng và chia việc cho subagent.", en: "Packaging skills and delegating to subagents." } },
  { title: "Introduction to Model Context Protocol", desc: { vi: "Xây MCP server: tools, resources, prompts.", en: "Building MCP servers: tools, resources, prompts." } },
  { title: "MCP: Advanced Topics", desc: { vi: "Transport, sampling, triển khai MCP ở production.", en: "Transports, sampling, production MCP." } },
  { title: "Building with the Claude API", desc: { vi: "Messages API, tool use, prompt caching, RAG, agent.", en: "Messages API, tool use, prompt caching, RAG, agents." } },
  { title: "Claude on Amazon Bedrock / Google Vertex AI", desc: { vi: "Dùng Claude qua nền tảng đám mây.", en: "Using Claude through cloud platforms." } },
];

function Ext({ href, children }: { href: string; children: React.ReactNode }) {
  return <a href={href} target="_blank" rel="noreferrer">{children} <ExternalLink size={14} aria-hidden="true" /></a>;
}

export function ResourcesPage() {
  const t = useT();
  const lang = useLang();
  useDocumentTitle(t("Tài liệu chính thức", "Official resources"));
  return (
    <>
      <h1>{t("Tài liệu chính thức", "Official resources")}</h1>
      <p className="muted">{t("Nguồn gốc của mọi nội dung trong ứng dụng — hãy đọc exam guide của chứng chỉ bạn định thi.", "The sources behind everything in this app — read the exam guide for the certification you're targeting.")}</p>
      <GuideTabs />

      <h2>{t("Chương trình chứng chỉ", "Certification program")}</h2>
      <div className="card">
        <ul className="mb-0">
          <li><Ext href={PROGRAM_DOCS.certificationsUrl}>{t("Trang chứng chỉ Partner", "Partner certifications page")}</Ext></li>
          <li><Ext href={PROGRAM_DOCS.faqUrl}>{t("FAQ chứng chỉ", "Certification FAQ")}</Ext></li>
          <li><Ext href={PROGRAM_DOCS.prepCoursesUrl}>{t("Khóa luyện thi chính thức", "Official exam prep courses")}</Ext></li>
          <li><Ext href={PROGRAM_DOCS.catalogUrl}>{t("Danh mục khóa học Partner Academy", "Partner Academy course catalog")}</Ext></li>
          <li><Ext href={PROGRAM_DOCS.policyUrl}>{t("Chính sách kỳ thi (PDF)", "Exam policy (PDF)")}</Ext></li>
          <li><Ext href={PROGRAM_DOCS.termsUrl}>{t("Điều khoản chứng chỉ (PDF)", "Certification terms (PDF)")}</Ext></li>
          <li><Ext href={PROGRAM_DOCS.pearsonUrl}>Pearson VUE – Anthropic</Ext></li>
        </ul>
      </div>

      <div className="section-title"><h2>{t("Theo từng chứng chỉ", "By certification")}</h2></div>
      <div className="grid grid-2">
        {EXAMS.map((e) => (
          <div key={e.id} className="card">
            <div className="row"><ExamBadge examId={e.id} size={28} /><strong>{e.code}</strong></div>
            <ul className="small mb-0 mt">
              {e.resources.map((r) => <li key={r.url + r.title.en}><Ext href={r.url}>{pick(r.title, lang)}</Ext></li>)}
            </ul>
          </div>
        ))}
      </div>

      <div className="section-title"><h2>{t("Khóa học miễn phí – Anthropic Academy", "Free courses – Anthropic Academy")}</h2></div>
      <div className="grid grid-2">
        {COURSES.map((c) => (
          <a key={c.title} href={PROGRAM_DOCS.publicAcademyUrl} target="_blank" rel="noreferrer" className="card link">
            <strong lang="en">{c.title}</strong>
            <p className="small muted mb-0">{pick(c.desc, lang)}</p>
          </a>
        ))}
      </div>
      <p className="tiny muted mt">{t("Ứng dụng độc lập, không liên kết với Anthropic.", "Independent app, not affiliated with Anthropic.")}</p>
    </>
  );
}
